import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import {
	Appointment,
	AppointmentResponse,
} from 'src/shared/models/appointment.model';
import {
	ApproverAssignmentsResponse,
	UpdatedApproverAssignment,
} from 'src/shared/models/approver-assignment.model';
import { BaseResponse } from 'src/shared/models/base-response.model';
import {
	CallingTreeAssignmentsResponse,
	UpdatedCallingTreeAssignments,
} from 'src/shared/models/calling-tree-assignments.model';
import {
	Dependent,
	DependentResponse,
} from 'src/shared/models/dependent.model';
import { EmergencyInfoResponse } from 'src/shared/models/emergency-info.model';
import {
	HousingUnit,
	HousingUnitResponse,
} from 'src/shared/models/housing-unit.model';
import { Housing, HousingResponse } from 'src/shared/models/housing.model';
import {
	Insurance,
	InsuranceResponse,
} from 'src/shared/models/insurance.model';
import { Pet, PetResponse } from 'src/shared/models/pet.model';
import {
	Residency,
	ResidencyResponse,
} from 'src/shared/models/residency.model';
import {
	UserDropDown,
	UserDropDownResponse,
} from 'src/shared/models/user-drop-down.model';
import {
	UserPassport,
	UserPassportResponse,
} from 'src/shared/models/user-passport.model';
import {
	UserPhoneInfo,
	UserPhoneInfoResponse,
} from 'src/shared/models/user-phone-info.model';
import {
	UserSearch,
	UserSearchResponse,
} from 'src/shared/models/user-search.model';
import { UserResponse } from 'src/shared/models/user.model';
import { BackendService } from './backend.service';

const API_URL = `${environment.apiUrl}/web/user`;

export const USER_URL = `${API_URL}/user`;
export const USER_RESPONSE = 'Fetched User';

export const USER_PHONE_INFO_URL = `${API_URL}/userphoneinfo`;
export const USER_PHONE_INFO_RESPONSE = 'Fetched User Phone Info';

export const USER_PASSPORTS_URL = `${API_URL}/userpassports`;
export const USER_PASSPORTS_RESPONSE = 'Fetched User Passports';

export const USER_APPOINTMENTS_URL = `${API_URL}/userappointments`;
export const USER_APPOINTMENTS_RESPONSE = 'Fetched User Appointments';

export const USER_RESIDENCY_URL = `${API_URL}/userresidency`;
export const USER_RESIDENCY_RESPONSE = 'Fetched User Residency';

export const USER_HOUSING_URL = `${API_URL}/userhousing`;
export const USER_HOUSING_RESPONSE = 'Fetched User Housing Info';

export const USER_PETS_URL = `${API_URL}/userpets`;
export const USER_PETS_RESPONSE = 'Fetched User Pets';

export const USER_INSURANCE_URL = `${API_URL}/userinsurance`;
export const USER_INSURANCE_RESPONSE = 'Fetched User Insurance Info';

export const USER_DEPENDENTS_URL = `${API_URL}/userdependents`;
export const USER_DEPENDENTS_RESPONSE = 'Fetched User Dependents';

export const USER_EMERGENCY_INFO_URL = `${API_URL}/useremergencyinfo`;
export const USER_EMERGENCY_INFO_RESPONSE = 'Fetched User Emergency Info';

export const SEARCH_URL = `${API_URL}/search`;
export const SEARCH_RESPONSE = 'Fetched Search';

export const MANAGER_DROP_DOWNS_URL = `${API_URL}/managerdropdowns`;
export const MANAGER_DROP_DOWNS_RESPONSE = 'Fetched Manager UserDropDowns';

export const AVAILABLE_HOUSING_UNITS_URL = `${API_URL}/availablehousingunits`;
export const AVAILABLE_HOUSING_UNITS_RESPONSE =
	'Fetched Available Housing Units';

export const APPROVER_ASSIGNMENTS_URL = `${API_URL}/approverassignments`;
export const APPROVER_ASSIGNMENTS_RESPONSE = 'Fetched Approver Assignments';

export const UPDATE_APPROVER_ASSIGNMENTS_URL = `${API_URL}/updateapproverassignments`;
export const UPDATE_APPROVER_ASSIGNMENTS_RESPONSE =
	'Updated Approver Assignments';

export const CALLING_TREE_ASSIGNMENTS_URL = `${API_URL}/callingtreeassignments`;
export const CALLING_TREE_ASSIGNMENTS_RESPONSE =
	'Fetched Calling Tree Assignments';

export const UPDATE_CALLING_TREE_ASSIGNMENTS_URL = `${API_URL}/updatecallingtreeassignments`;
export const UPDATE_CALLING_TREE_ASSIGNMENTS_RESPONSE =
	'Updated Calling Tree Assignments';

@Injectable({
	providedIn: 'root',
})
export class UserBackendService {
	private backend = inject(BackendService);

	/**
	 * User
	 * =====
	 * API Endpoint: /web/hr/user
	 * JSON Parameters: id
	 *
	 * Returns the user
	 */
	user(userId: number): Observable<UserResponse> {
		return this.backend
			.post(USER_URL, USER_RESPONSE, { id: userId })
			.pipe(map((response: UserResponse) => response));
	}

	/**
	 * User Phone Info
	 * ================
	 * API Endpoint: /web/hr/userphoneinfo
	 * JSON Parameters: id
	 *
	 * Selects a User's Phone Information
	 */
	userPhoneInfo(userId: number): Observable<UserPhoneInfo[]> {
		return this.backend
			.post(USER_PHONE_INFO_URL, USER_PHONE_INFO_RESPONSE, { id: userId })
			.pipe(
				map((response: UserPhoneInfoResponse) =>
					response.phone.map((json) => UserPhoneInfo.fromJSON(json))
				)
			);
	}

	/**
	 * User Passports
	 * ===============
	 * API Endpoint: /web/hr/userpassports
	 * JSON Parameters: id
	 *
	 * Selects a User's Passports
	 */
	userPassports(userId: number): Observable<UserPassport[]> {
		return this.backend
			.post(USER_PASSPORTS_URL, USER_PASSPORTS_RESPONSE, { id: userId })
			.pipe(
				map((response: UserPassportResponse) =>
					response.passports.map((json) =>
						UserPassport.fromJSON(json)
					)
				)
			);
	}

	/**
	 * User Appointments
	 * ==================
	 * API Endpoint: /web/hr/userappointments
	 * JSON Parameters: id
	 *
	 * Selects a User's Appointments
	 */
	userAppointments(userId: number): Observable<Appointment[]> {
		return this.backend
			.post(USER_APPOINTMENTS_URL, USER_APPOINTMENTS_RESPONSE, {
				id: userId,
			})
			.pipe(
				map((response: AppointmentResponse) =>
					response.appointments.map((json) =>
						Appointment.fromJSON(json)
					)
				)
			);
	}

	/**
	 * User Residency
	 * ===============
	 * API Endpoint: /web/hr/userresidency
	 * JSON Parameters: id
	 *
	 * Selects a User's Residency Information
	 */
	userResidency(userId: number): Observable<Residency> {
		return this.backend
			.post(USER_RESIDENCY_URL, USER_RESIDENCY_RESPONSE, {
				id: userId,
			})
			.pipe(
				map((response: ResidencyResponse) =>
					Residency.fromJSON(response.residency)
				)
			);
	}

	/**
	 * User Housing Info
	 * ==================
	 * API Endpoint: /web/hr/userhousing
	 * JSON Parameters: id
	 *
	 * Selects a User's Housing Information
	 */
	userHousing(userId: number): Observable<Housing> {
		return this.backend
			.post(USER_HOUSING_URL, USER_HOUSING_RESPONSE, { id: userId })
			.pipe(
				map((response: HousingResponse) =>
					Housing.fromJSON(response.housing)
				)
			);
	}

	/**
	 * User Pets
	 * ==========
	 * API Endpoint: /web/hr/userpets
	 * JSON Parameters: id
	 *
	 * Selects a User's Pets
	 */
	userPets(userId: number): Observable<Pet[]> {
		return this.backend
			.post(USER_PETS_URL, USER_PETS_RESPONSE, { id: userId })
			.pipe(
				map((response: PetResponse) =>
					response.pets.map((json) => Pet.fromJSON(json))
				)
			);
	}

	/**
	 * User Insurance
	 * ===============
	 * API Endpoint: /web/hr/userinsurance
	 * JSON Parameters: id
	 *
	 * Selects a User's Insurance Info
	 */
	userInsurance(userId: number): Observable<Insurance> {
		return this.backend
			.post(USER_INSURANCE_URL, USER_INSURANCE_RESPONSE, { id: userId })
			.pipe(
				map((response: InsuranceResponse) =>
					Insurance.fromJSON(response.insurance)
				)
			);
	}

	/**
	 * User Dependents
	 * ================
	 * API Endpoint: /web/hr/userdependents
	 * JSON Parameters: id
	 *
	 * Selects a User's Dependents w/ Passport Information
	 */
	userDependents(userId: number): Observable<Dependent[]> {
		return this.backend
			.post(USER_DEPENDENTS_URL, USER_DEPENDENTS_RESPONSE, { id: userId })
			.pipe(
				map((response: DependentResponse) =>
					response.dependents.map((json) => Dependent.fromJSON(json))
				)
			);
	}

	/**
	 * User Emergency Info
	 * ====================
	 * API Endpoint: /web/hr/useremergencyinfo
	 * JSON Parameters: id
	 *
	 * Select's a User's Emergency Information
	 */
	userEmergencyInfo(userId: number): Observable<EmergencyInfoResponse> {
		return this.backend
			.post(USER_EMERGENCY_INFO_URL, USER_EMERGENCY_INFO_RESPONSE, {
				id: userId,
			})
			.pipe(map((response: EmergencyInfoResponse) => response));
	}

	/**
	 * Search
	 * =======
	 * API Endpoint: /web/hr/search
	 * JSON Parameters: UserSearch
	 *
	 * Returns the result of the search query based upon the provided search values
	 */
	search(search: UserSearch): Observable<UserSearchResponse> {
		return this.backend
			.post(SEARCH_URL, SEARCH_RESPONSE, search)
			.pipe(map((r: UserSearchResponse) => r));
	}

	/**
	 * Manager Drop-Downs
	 * ===================
	 * API Endpoint: /web/hr/managerdropdowns
	 *
	 * Returns an array of UserDropDown objects of only Managers
	 */
	getManagerDropDowns(): Observable<UserDropDown[]> {
		return this.backend
			.post(MANAGER_DROP_DOWNS_URL, MANAGER_DROP_DOWNS_RESPONSE, {})
			.pipe(
				map((response: UserDropDownResponse) =>
					response.dropdowns.map((json) =>
						UserDropDown.fromJSON(json)
					)
				)
			);
	}

	/**
	 * Available Housing Units
	 * ========================
	 * API Endpoint: /web/hr/availablehousingunits
	 * JSON Parameters: id
	 *
	 * Returns an array of HousingUnit objects for the passed user id
	 */
	getAvailableHousingUnits(userId: number): Observable<HousingUnit[]> {
		return this.backend
			.post(
				AVAILABLE_HOUSING_UNITS_URL,
				AVAILABLE_HOUSING_UNITS_RESPONSE,
				{ id: userId }
			)
			.pipe(
				map((response: HousingUnitResponse) =>
					response.housingUnits.map((json) =>
						HousingUnit.fromJSON(json)
					)
				)
			);
	}

	/**
	 * Approver Assignments for User
	 * ==============================
	 * API Endpoint: /web/user/approverassignments
	 * JSON Parameters: id
	 *
	 * Returns the ApproverAssignmentsResponse for the provided User
	 */
	getApproverAssignments(
		userId: number
	): Observable<ApproverAssignmentsResponse> {
		return this.backend
			.post(APPROVER_ASSIGNMENTS_URL, APPROVER_ASSIGNMENTS_RESPONSE, {
				id: userId,
			})
			.pipe(map((response: ApproverAssignmentsResponse) => response));
	}

	/**
	 * Update Approver Assignments
	 * ===========================
	 * API Endpoint: /web/user/updateapproverassignments
	 * JSON Parameters: assignments: UpdatedApproverAssignment[]
	 *
	 * Updates for a previously returned list of ApproverAssignments
	 */
	updateApproverAssignments(
		assignments: UpdatedApproverAssignment[]
	): Observable<BaseResponse> {
		return this.backend.post(
			UPDATE_APPROVER_ASSIGNMENTS_URL,
			UPDATE_APPROVER_ASSIGNMENTS_RESPONSE,
			{ assignments }
		);
	}

	/**
	 * Calling Tree Assignments for User
	 * ==================================
	 * API Endpoint: /web/user/callingtreeassignments
	 * JSON Parameters: id
	 *
	 * Returns the CallingTreeAssignmentsResponse for the provided User
	 */
	getCallingTreeAssignments(
		userId: number
	): Observable<CallingTreeAssignmentsResponse> {
		return this.backend
			.post(
				CALLING_TREE_ASSIGNMENTS_URL,
				CALLING_TREE_ASSIGNMENTS_RESPONSE,
				{
					id: userId,
				}
			)
			.pipe(map((response: CallingTreeAssignmentsResponse) => response));
	}

	/**
	 * Update Calling Tree Assignments
	 * ================================
	 * API Endpoint: /web/user/updatecallingtreeassignments
	 * JSON Parameters: assignments: UpdatedCallingTreeAssignments
	 *
	 * Updates for the previously returned CallingTreeAssignments
	 */
	updateCallingTreeAssignments(
		assignments: UpdatedCallingTreeAssignments
	): Observable<BaseResponse> {
		return this.backend.post(
			UPDATE_CALLING_TREE_ASSIGNMENTS_URL,
			UPDATE_CALLING_TREE_ASSIGNMENTS_RESPONSE,
			assignments
		);
	}
}
